import { useState, useRef, useEffect } from 'preact/hooks';
import { useSshVoiceHistory, SSH_PRIMARY_TERMINAL_ID } from '../state/sshTerminalStore';

interface Props {
  /** List of voice-transcribed commands (most recent last) */
  history: string[];
  /** Resend a command to the SSH PTY */
  onResend: (text: string) => void;
}

/**
 * Horizontal scrollable strip of voice command chips, shown above the SSH PTT button.
 * Tap a chip to resend the command. Long-press to edit before resending.
 */
export function SshVoiceHistory({ history: propHistory, onResend }: Props) {
  // ssh-terminal-fsm-migration Phase 1 (read migration): read from the FSM store,
  // falling back to the legacy prop. The useSocket transcript handler dual-writes
  // both (consecutive-dedup mirrored), so they are equivalent; the fallback hedges
  // any store gap until Phase 2 makes the store authoritative.
  const storeHistory = useSshVoiceHistory(SSH_PRIMARY_TERMINAL_ID);
  const history = storeHistory.length ? storeHistory : propHistory;

  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<number | null>(null);

  // Auto-scroll to the right (most recent) when history changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [history]);

  if (history.length === 0) return null;

  // Show last 8 commands (most recent on the right)
  const visible = history.slice(-8);
  const baseIdx = history.length - visible.length;

  const handlePointerDown = (idx: number) => {
    longPressTimer.current = window.setTimeout(() => {
      // Long press — open inline editor
      setEditIdx(idx);
      setEditText(history[idx]);
      longPressTimer.current = null;
    }, 500);
  };

  const handlePointerUp = (idx: number) => {
    if (longPressTimer.current !== null) {
      // Short tap — resend immediately
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      if (editIdx === null) {
        onResend(history[idx] + '\n');
      }
    }
  };

  const handlePointerLeave = () => {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleEditSend = () => {
    if (editText.trim()) {
      onResend(editText.trim() + '\n');
    }
    setEditIdx(null);
    setEditText('');
  };

  const handleEditKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSend();
    } else if (e.key === 'Escape') {
      setEditIdx(null);
    }
  };

  return (
    <div class="ssh-voice-history-container">
      {editIdx !== null && (
        <div class="ssh-voice-edit">
          <input
            class="ssh-voice-edit-input"
            type="text"
            value={editText}
            onInput={(e) => setEditText((e.target as HTMLInputElement).value)}
            onKeyDown={handleEditKeyDown}
            autocapitalize="off"
            autocorrect="off"
            autocomplete="off"
            spellcheck={false}
            autofocus
          />
          <button class="ssh-voice-edit-send" onClick={handleEditSend}>&#10148;</button>
          <button class="ssh-voice-edit-cancel" onClick={() => setEditIdx(null)}>&#10005;</button>
        </div>
      )}
      <div class="ssh-voice-history" ref={scrollRef}>
        {visible.map((cmd, i) => (
          <button
            key={`${baseIdx + i}-${cmd}`}
            class="ssh-voice-chip"
            onPointerDown={() => handlePointerDown(baseIdx + i)}
            onPointerUp={() => handlePointerUp(baseIdx + i)}
            onPointerLeave={handlePointerLeave}
            title={cmd}
          >
            {cmd.length > 30 ? cmd.slice(0, 28) + '…' : cmd}
          </button>
        ))}
      </div>
    </div>
  );
}
